import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/components/booking/constants";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import DatePickerAU from "@/components/DatePickerAU";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, Layers, Camera, CheckCircle2, AlertTriangle, PackageCheck, MapPin, CalendarClock, ExternalLink } from "lucide-react";
import { isCbrPickup, isCbrDeliveryTown, isCbrDay, CBR_WINDOW, CBR_DAYS, CBR_NOTE } from "@/components/booking/constants";
import { toast } from "sonner";
import { UrgentAck } from "@/components/booking/UrgentAck";

const PICKUP_WINDOWS = [
  { v: "Anytime (business hours)", l: "Anytime (business hours)" },
  { v: "Morning (8am–12pm)", l: "Morning (8am–12pm)" },
  { v: "Afternoon (12pm–4pm)", l: "Afternoon (12pm–4pm)" },
  { v: "Late (4pm–6pm)", l: "Late (4pm–6pm)" },
];
const fmtAddr = (a) => (a ? [a.business, a.line1, a.city, a.postcode].filter(Boolean).join(", ") : "—") || "—";
const CONF = {
  high: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-red-100 text-red-800 border-red-200",
};

export default function BulkBooking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowed = ["on_account", "paint_shop"].includes(user?.customer_type) || user?.role === "admin";
  const [scanning, setScanning] = useState(false);
  const [rows, setRows] = useState([]); // scan results with `include`
  const [service, setService] = useState("standard");
  const [urgentAck, setUrgentAck] = useState(false);
  const [schedule, setSchedule] = useState({ date: "", window: "" });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState([]);
  const fileRef = useRef(null);
  const cbrAny = rows.some((r) => r.include && r.ok && isCbrPickup(r.pickup));

  useEffect(() => {
    if (cbrAny) setSchedule((s) => ({ date: isCbrDay(s.date) ? s.date : "", window: CBR_WINDOW }));
    else setSchedule((s) => (s.window === CBR_WINDOW ? { ...s, window: "" } : s));
  }, [cbrAny]);

  if (!allowed) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <main className="mx-auto max-w-2xl px-4 py-16 text-center">
          <p className="text-muted-foreground">Bulk consignments are available for account holders.</p>
          <Button onClick={() => navigate("/book")} className="mt-4 bg-kilted-green hover:bg-kilted-greenLight">Go to booking</Button>
        </main>
      </div>
    );
  }

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setScanning(true);
    setCreated([]);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/consignment/bulk-scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows((data.results || []).map((r) => ({ ...r, include: !!r.ok })));
      toast.success(`Scanned ${data.count} label${data.count === 1 ? "" : "s"} — review below.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk scan failed");
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = (idx) => setRows((prev) => prev.map((r, i) => (i === idx && r.ok ? { ...r, include: !r.include } : r)));
  const needsFix = (a) => !a || a.lat == null || a.lng == null || a.needs_review;
  const fixAddress = (idx, which, addr) => setRows((prev) => prev.map((r, i) => {
    if (i !== idx) return r;
    const merged = { ...(r[which] || {}), ...addr, needs_review: false };
    const next = { ...r, [which]: merged };
    const ok = !needsFix(next.pickup) && !needsFix(next.dropoff);
    if (ok && !r.ok) toast.success("Label fixed — ready to book");
    return { ...next, ok, include: ok };
  }));
  const selectedCount = rows.filter((r) => r.include && r.ok).length;

  const createAll = async () => {
    const picks = rows.filter((r) => r.include && r.ok);
    if (!picks.length) { toast.error("Select at least one verified label"); return; }
    setCreating(true);
    const results = [];
    for (const r of picks) {
      try {
        const { data } = await api.post("/orders", {
          pickup: r.pickup,
          dropoff: r.dropoff,
          service,
          urgent_ack: service === "sameday" && urgentAck,
          items: (r.items || []).map((i) => ({ size: i.size, quantity: i.quantity, weight_kg: i.weight_kg, parcel_description: i.parcel_description })),
          scheduled_date: schedule.date,
          pickup_window: schedule.window,
        });
        results.push({ ok: true, filename: r.filename, id: data.id, tracking: data.tracking_number });
      } catch (err) {
        results.push({ ok: false, filename: r.filename, error: formatApiErrorDetail(err.response?.data?.detail) || "Failed" });
      }
    }
    setCreated(results);
    setCreating(false);
    const ok = results.filter((x) => x.ok).length;
    if (ok) toast.success(`Created ${ok} consignment${ok === 1 ? "" : "s"}`);
    if (ok < results.length) toast.error(`${results.length - ok} could not be created`);
    setRows((prev) => prev.map((r) => (results.find((x) => x.ok && x.filename === r.filename) ? { ...r, done: true, include: false } : r)));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/book")} data-testid="bulk-back-button" className="mb-4 gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back to single booking
        </Button>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-extrabold tracking-tight text-kilted-green">
          <Layers className="h-7 w-7 text-kilted-gold" /> Bulk consignments
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Upload several label photos at once — we'll read each one and create a consignment per label for your run.</p>

        <Card className="mt-6 border-kilted-gold/40 bg-kilted-gold/5">
          <CardContent className="pt-6">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="bulk-files-input" />
            <Button type="button" disabled={scanning} onClick={() => fileRef.current?.click()} data-testid="bulk-upload-button" className="w-full gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {scanning ? "Reading labels…" : "Upload label photos"}
            </Button>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <>
            <Card className="mt-6">
              <CardHeader><CardTitle className="text-base">Shared options</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                {cbrAny && (
                  <p className="rounded-md border border-kilted-gold/40 bg-kilted-gold/10 px-2.5 py-2 text-xs text-kilted-green sm:col-span-3" data-testid="bulk-cbr-note">
                    {CBR_NOTE} One or more selected labels pick up in Canberra/Queanbeyan, so this whole batch is locked to those times — untick them or book non-Canberra labels separately if they need a different day.
                  </p>
                )}
                <div className="space-y-1.5">
                  <Label>Service level</Label>
                  <Select value={service} onValueChange={(v) => { setService(v); setUrgentAck(false); }}>
                    <SelectTrigger data-testid="bulk-service-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Pickup date</Label>
                  <DatePickerAU value={schedule.date} onChange={(d) => setSchedule((s) => ({ ...s, date: d }))} minToday allowedDays={cbrAny ? CBR_DAYS : undefined} placeholder="Any day" testid="bulk-date-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Time window</Label>
                  {cbrAny ? (
                    <div className="flex h-9 items-center rounded-md border border-input bg-muted/50 px-3 text-sm" data-testid="bulk-window-locked">{CBR_WINDOW}</div>
                  ) : (
                    <Select value={schedule.window} onValueChange={(v) => setSchedule((s) => ({ ...s, window: v }))}>
                      <SelectTrigger data-testid="bulk-window-select"><SelectValue placeholder="Any time" /></SelectTrigger>
                      <SelectContent>{PICKUP_WINDOWS.map((w) => <SelectItem key={w.v} value={w.v}>{w.l}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="mt-6 space-y-3" data-testid="bulk-results">
              {rows.map((r, idx) => (
                <Card key={`${r.filename}-${idx}`} className={r.done ? "opacity-60" : ""} data-testid={`bulk-row-${idx}`}>
                  <CardContent className="flex items-start gap-3 pt-6">
                    <input type="checkbox" checked={!!r.include} disabled={!r.ok || r.done} onChange={() => toggle(idx)} data-testid={`bulk-include-${idx}`} className="mt-1 h-4 w-4 accent-kilted-green" />
                    <div className="min-w-0 flex-1">
                      {r.error ? (
                        <p className="flex items-center gap-2 text-sm text-red-700"><AlertTriangle className="h-4 w-4" /> {r.filename}: {r.error}</p>
                      ) : (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-xs text-muted-foreground">{r.filename}</span>
                            <Badge variant="outline" className={CONF[r.confidence]} data-testid={`bulk-confidence-${idx}`}>{r.confidence}</Badge>
                            {r.ok ? (
                              <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-emerald-800"><CheckCircle2 className="mr-1 h-3 w-3" />Verified</Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-200 bg-amber-100 text-amber-800" data-testid={`bulk-blocked-${idx}`}><AlertTriangle className="mr-1 h-3 w-3" />Needs address fix</Badge>
                            )}
                          </div>
                          <div className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                            <p className="flex items-start gap-1.5"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-kilted-green" /><span><span className="font-semibold">From:</span> {fmtAddr(r.pickup)}</span></p>
                            <p className="flex items-start gap-1.5"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-kilted-gold" /><span><span className="font-semibold">To:</span> {fmtAddr(r.dropoff)}</span></p>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {(r.items || []).map((i) => `${i.quantity}× ${i.size} · ${i.weight_kg}kg${i.parcel_description ? ` (${i.parcel_description})` : ""}`).join("; ")}
                          </p>
                          {r.ok && !r.done && isCbrPickup(r.pickup) && !isCbrDeliveryTown(r.dropoff) && (
                            <p className="mt-2 flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-700" data-testid={`bulk-cbr-warning-${idx}`}>
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Canberra &amp; Queanbeyan pickups are delivered to Cooma and the Jindabyne area only — fix the drop-off town or this label will be rejected.
                            </p>
                          )}
                          {!r.ok && !r.done && (
                            <div className="mt-3 space-y-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3" data-testid={`bulk-fix-${idx}`}>
                              <p className="text-xs font-semibold text-amber-800">Couldn't verify this label's address — search and pick the correct one below to make it bookable.</p>
                              {needsFix(r.pickup) && <AddressAutocomplete label="Fix pickup address" onSelect={(a) => fixAddress(idx, "pickup", a)} testid={`bulk-fix-pickup-${idx}`} />}
                              {needsFix(r.dropoff) && <AddressAutocomplete label="Fix drop-off address" onSelect={(a) => fixAddress(idx, "dropoff", a)} testid={`bulk-fix-dropoff-${idx}`} />}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {service === "sameday" && <UrgentAck checked={urgentAck} onChange={setUrgentAck} testid="bulk-urgent-ack-checkbox" />}
            <div className="sticky bottom-4 mt-6 flex items-center justify-between rounded-xl border border-kilted-green/20 bg-white p-4 shadow-lg">
              <p className="text-sm font-medium">{selectedCount} verified label{selectedCount === 1 ? "" : "s"} selected</p>
              <Button onClick={createAll} disabled={creating || !selectedCount || (service === "sameday" && !urgentAck)} data-testid="bulk-create-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Create {selectedCount} consignment{selectedCount === 1 ? "" : "s"}
              </Button>
            </div>
          </>
        )}

        {created.length > 0 && (
          <Card className="mt-6 border-emerald-200" data-testid="bulk-created">
            <CardHeader><CardTitle className="text-base">Created consignments</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {created.map((c, i) => (
                <div key={`${c.tracking || c.filename}-${i}`} className="flex items-center justify-between text-sm">
                  {c.ok ? (
                    <>
                      <span className="flex items-center gap-2 text-emerald-800"><CheckCircle2 className="h-4 w-4" /> {c.tracking} <span className="text-muted-foreground">({c.filename})</span></span>
                      <button onClick={() => navigate(`/orders/${c.id}`)} data-testid={`bulk-open-${i}`} className="flex items-center gap-1 font-medium text-kilted-green hover:underline">Open <ExternalLink className="h-3.5 w-3.5" /></button>
                    </>
                  ) : (
                    <span className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-4 w-4" /> {c.filename}: {c.error}</span>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
